import type { ApolloError } from "@apollo/client/core";
import type { GraphQLFormattedError } from "graphql";

/**
 * Returns the human-readable message of one GraphQL error.
 *
 * NestJS puts the real reason in `extensions.originalError.message` (a string, or a
 * `string[]` from ValidationPipe) and leaves the top-level `message` as a generic
 * "Bad Request Exception". The first original message wins, with its first letter
 * capitalised, since it starts with a lower-case field name ("label must not…").
 */
export const readableGraphQLErrorMessage = (
  gqlError: Pick<GraphQLFormattedError, "message" | "extensions">,
): string => {
  const original = (
    gqlError.extensions?.originalError as { message?: unknown } | undefined
  )?.message;
  const first = Array.isArray(original) ? original[0] : original;

  if (typeof first === "string" && first) return first.charAt(0).toUpperCase() + first.slice(1);
  return gqlError.message;
};

/**
 * Returns the first human-readable message from a NestJS BAD_REQUEST Apollo error,
 * or `fallback` when the error carries no GraphQL errors.
 */
export const extractFirstErrorMessage = (
  error: ApolloError,
  fallback = "Something went wrong.",
): string => {
  const gqlError = error.graphQLErrors[0];
  if (!gqlError) return fallback;
  return readableGraphQLErrorMessage(gqlError) || fallback;
};

/**
 * Parses field-level validation messages from a NestJS BAD_REQUEST Apollo error.
 *
 * NestJS ValidationPipe surfaces errors as:
 *   extensions.originalError.message: string[]
 * where each entry starts with the field name, e.g.:
 *   "code must be snake_case..."
 *   "label must be longer than 3 characters"
 *
 * Returns a map of { fieldName: string[] } so callers can surface
 * individual messages on the corresponding form fields.
 *
 * @example
 * onCreateError((error) => {
 *   const fieldErrors = extractFieldErrors(error)
 *   if (fieldErrors["code"]?.[0]) drawerRef.value?.setCodeError(fieldErrors["code"][0])
 * })
 */
export const extractFieldErrors = (error: ApolloError): Record<string, string[]> => {
  const fields: Record<string, string[]> = {};

  for (const gqlError of error.graphQLErrors) {
    const messages = (
      gqlError.extensions?.originalError as { message?: unknown } | undefined
    )?.message;

    if (!Array.isArray(messages)) continue;

    for (const msg of messages) {
      if (typeof msg !== "string") continue;
      const field = msg.split(" ")[0];
      if (!field) continue;
      fields[field] ??= [];
      fields[field].push(msg);
    }
  }

  return fields;
};

/**
 * Every message a NestJS BAD_REQUEST Apollo error carries, in the order the server sent them.
 *
 * Unlike `extractFieldErrors`, nothing is split off the front: use this when the messages are
 * about items rather than form fields, and the caller matches them itself.
 */
export const extractErrorMessages = (error: ApolloError): string[] =>
  error.graphQLErrors.flatMap((gqlError) => {
    // `extensions` is typed as a plain record; NestJS puts the validation messages in here.
    const messages = (
      gqlError.extensions?.originalError as { message?: unknown } | undefined
    )?.message;

    if (!Array.isArray(messages)) return [];
    return messages.filter((m): m is string => typeof m === "string");
  });

/**
 * Messages that each name one item of a list in double quotes, keyed by that item in lower case,
 * so a list editor can show every message under the item it is about. The `<field>: ` prefix the
 * server writes in front is dropped; a message that names no item is left out.
 *
 * @example
 * // server: 'options: "Red" is used by 12 products and cannot be removed.'
 * extractQuotedItemErrors(error, "options")
 * // → { red: '"Red" is used by 12 products and cannot be removed.' }
 */
export const extractQuotedItemErrors = (
  error: ApolloError,
  field: string,
): Record<string, string> => {
  const prefix = `${field}: `;
  const items: Record<string, string> = {};

  for (const message of extractErrorMessages(error)) {
    const item = /"([^"]+)"/.exec(message)?.[1];
    if (!item) continue;
    items[item.toLowerCase()] = message.startsWith(prefix) ? message.slice(prefix.length) : message;
  }

  return items;
};

// ── Attribute errors ──────────────────────────────────────────────────────────

interface AttributeValidationError {
  field: string
  message: string
  context: string
}

const isAttributeValidationError = (val: unknown): val is AttributeValidationError =>
  typeof val === "object"
  && val !== null
  && typeof (val as Record<string, unknown>).field === "string"
  && typeof (val as Record<string, unknown>).message === "string"
  && typeof (val as Record<string, unknown>).context === "string";

const LEGACY_ATTRIBUTE_PATTERN = /attribute '([^']+)'\s+[—–-]+\s+(.+)/;

/**
 * Parses attribute-level validation errors from product/variant mutations.
 *
 * Supports two error formats emitted by the backend:
 *
 * NEW (structured) — preferred, checked first:
 *   extensions.originalError.errors[{ field, message, context }]
 *   `field` is the attribute code. `context` identifies the owning entity.
 *   Pass `contexts` to restrict which entities to surface — omit to include all.
 *
 * LEGACY (text-parsed) — fallback for backward compat during the migration period:
 *   gqlError.message matching /attribute '([^']+)'\s+[—–-]+\s+(.+)/
 *   Context filtering is not applied to the legacy format (no context field available).
 *
 * When the same field appears in multiple errors, the first message wins.
 * Returns a map of { attributeCode: errorMessage }.
 *
 * @example
 * // All contexts (default — call site in ProductDetailFeature unchanged)
 * onSaveError((error) => {
 *   const attrErrors = extractAttributeErrors(error)
 *   store.setAttributeErrors(attrErrors)
 * })
 *
 * @example
 * // Only product-context errors (explicit opt-in)
 * onSaveError((error) => {
 *   const attrErrors = extractAttributeErrors(error, ["product"])
 *   store.setAttributeErrors(attrErrors)
 * })
 */
export const extractAttributeErrors = (
  error: ApolloError,
  contexts?: string[],
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const gqlError of error.graphQLErrors) {
    // ── New structured format ────────────────────────────────────────────────
    const rawErrors = (
      gqlError.extensions?.originalError as { errors?: unknown } | undefined
    )?.errors;

    if (Array.isArray(rawErrors)) {
      for (const e of rawErrors) {
        if (!isAttributeValidationError(e)) continue;
        if (contexts !== undefined && !contexts.includes(e.context)) continue;
        if (!(e.field in result)) result[e.field] = e.message;
      }
      continue;
    }

    // ── Legacy text-parsed format ────────────────────────────────────────────
    const match = LEGACY_ATTRIBUTE_PATTERN.exec(gqlError.message);
    if (match) {
      const [, code, message] = match;
      if (code && message && !(code in result)) result[code] = message;
    }
  }

  return result;
};
